/**
 * Display-label helpers for backend enum-ish strings (order status,
 * handoff state, role, severity, …).
 *
 * Single source of truth: the `admin:<map>.<value>` catalogs. Every helper
 * goes through {@link enumLabel}, which looks the value up with a
 * `defaultValue` that runs `humanize()` on the raw token — so the UI never
 * leaks `awaiting_inbound_delivery` even for a value nobody translated yet.
 * That fallback is why a NEW backend enum value degrades to readable text
 * instead of a raw identifier; adding the i18n key is then an improvement,
 * not a bug fix.
 *
 * Open-ended vocabularies (audit `action` / `resource_type`, lifecycle
 * `event_type`) deliberately have NO catalog — their cardinality grows with
 * the backend, so they rely on `humanize()` alone.
 *
 * Process names are NOT i18n keys — they come from the fetched process
 * row (see `useProcessTypeLabel` in `lib/useProcessTypeLabel.ts`).
 */
/**
 * Structural `t`. Accepts i18next's branded `TFunction` AND the local
 * `type TFn = (key, opts) => string` aliases that several admin pages
 * declare to pass `t` down as a prop — those pages could not call these
 * helpers before, which is one reason they hand-rolled raw enum output.
 */
export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/** snake_case / kebab-case → "Title case" so unknown values still read. */
export function humanize(value: string): string {
  if (!value) return "";
  const cleaned = value.replace(/[_-]+/g, " ").trim();
  if (!cleaned) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Resolve one backend enum token against the `admin:<map>` catalog.
 *
 * The single implementation behind every `format*` helper below — they used
 * to be seven copies of this body, which is how `{row.status}` kept getting
 * rendered raw next to a correctly-formatted sibling.
 */
export function enumLabel(
  map: string,
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  if (!value) return fallback ?? "—";
  return t(`${map}.${value}`, { ns: "admin", defaultValue: humanize(value) });
}

export function formatOrderStatus(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("orderStatus", value, t, fallback);
}

export function formatInvoiceType(
  value: string | null | undefined,
  t: TranslateFn,
): string {
  return enumLabel("invoiceType", value, t);
}

export function formatInvoiceStatus(
  value: string | null | undefined,
  t: TranslateFn,
): string {
  return enumLabel("invoiceStatus", value, t);
}

export function formatInvoicePaymentState(
  value: string | null | undefined,
  t: TranslateFn,
): string {
  return enumLabel("invoicePayment", value, t);
}

export function formatHandoffState(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("handoffState", value, t, fallback);
}

export function formatLifecycleStatus(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("lifecycleStatus", value, t, fallback);
}

/** `OrgRole` — admin / production / viewer. */
export function formatRole(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("role", value, t, fallback);
}

/** `Plan.tier` — free / pro / business / enterprise. */
export function formatPlanTier(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("planTier", value, t, fallback);
}

/** Purchase-order status — draft / sent / received / closed / … */
export function formatPurchaseOrderStatus(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("purchaseOrders.status", value, t, fallback);
}

/** Stock movement reason — received / reserved / consumed / … */
export function formatStockMovementReason(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("stockMovements.reason", value, t, fallback);
}

/** Lifecycle node kind — event / email / webhook / qc / … */
export function formatLifecycleNodeType(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("lifecycleNodeType", value, t, fallback);
}

/** Help-annotation variant — note / info / warning / alert. */
export function formatHelpAnnotationKind(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("helpAnnotationKind", value, t, fallback);
}

/** Help-annotation placement — marker (pinned "?") / callout (inline block). */
export function formatHelpAnnotationPlacement(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("helpAnnotationPlacement", value, t, fallback);
}

/** ``ProcessInputDimension.kind`` — material / machine / custom / file / … */
export function formatDimensionKind(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("dimensionKind", value, t, fallback);
}

/** ``ProcessInputDimension.renderer`` — select / number / swatch / … */
export function formatDimensionRenderer(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("dimensionRenderer", value, t, fallback);
}

/**
 * Permission ``(resource, action)`` — e.g. ``orders.write``.
 *
 * Composed rather than one catalog entry per pair: 12 resources × 5 verbs
 * would be 60 hand-written strings that drift, and a permission added
 * alongside a new route would render raw until someone remembered the
 * catalog. Composing means ``foo.read`` reads as "Foo — číst" on day one.
 *
 * ``permission.<resource>.<action>`` still wins when a specific pair needs
 * bespoke wording; otherwise the two halves resolve independently and each
 * falls back to ``humanize()``.
 */
export function formatPermission(
  resource: string,
  action: string,
  t: TranslateFn,
): string {
  const exact = t(`permission.${resource}.${action}`, {
    ns: "admin",
    defaultValue: "",
  });
  if (exact) return exact;
  return `${formatPermissionResource(resource, t)} — ${formatPermissionAction(
    action,
    t,
  )}`;
}

/** Resource half on its own — ``orders`` → "Objednávky". Used where the
 * surface already groups by resource and would otherwise repeat the name
 * on every action. */
export function formatPermissionResource(
  resource: string,
  t: TranslateFn,
): string {
  return enumLabel("permissionResource", resource, t);
}

/** Action half on its own — ``write`` → "upravovat". */
export function formatPermissionAction(
  action: string,
  t: TranslateFn,
): string {
  return enumLabel("permissionAction", action, t);
}

/**
 * Permission category — the section headings of the group editor. Values
 * come from ``lib/permissionGroups``, not from the backend.
 */
export function formatPermissionCategory(
  category: string,
  t: TranslateFn,
): string {
  return enumLabel("permissionCategory", category, t);
}

/** Lifecycle node visibility — internal / customer. */
export function formatLifecycleVisibility(
  value: string | null | undefined,
  t: TranslateFn,
  fallback?: string,
): string {
  return enumLabel("lifecycleVisibility", value, t, fallback);
}

/**
 * Kalendářní datum pro zákazníka („25. 9. 2026" / „Sep 25, 2026").
 *
 * KAN-1653 — ``/track`` a detail objednávky měly každý svou kopii
 * a lišily se v ``day`` (``2-digit`` vs. ``numeric``): tatáž objednávka
 * ukazovala „01. 10. 2026" na jedné stránce a „1. 10. 2026" na druhé.
 */
export function formatCalendarDate(
  iso: string,
  locale: string,
  timeZone?: string | null,
): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // KAN-1850 — den v pásmu OBCHODU (``shop_timezone`` z API), ne v pásmu
  // prohlížeče: zákazník v jiném pásmu jinak vidí jiný den než obchod slíbil.
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

/**
 * Format a date-time stamp using the app locale. The native
 * `Date#toLocaleString()` ignores the app locale and falls back to the
 * browser default — visible to a CS user as "5/25/2026, 12:00:00 AM".
 */
export function formatDateTime(
  iso: string | null | undefined,
  locale: string,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

/**
 * „v3" → „3". Štítek verze mintuje server jako ``v<N>``; čtenář dokumentu
 * ale čte „Verze 3", ne „Verze v3". Starší ručně psané štítky
 * (``2026-08``) se vydají beze změny. Čte to stránka dokumentu
 * i stránka Bezpečnost, aby obě psaly totéž číslo (KAN-1937).
 */
export function legalVersionNumber(label: string): string {
  const m = /^v(\d+)$/.exec(label);
  return m?.[1] ?? label;
}
